import { Fragment, useCallback, useEffect, useState } from 'react'
import { api, type AuditEntry } from '../api'
import { RowMenu } from '../components/RowMenu'

/**
 * The audit trail — TECHNICAL_DESIGN.md §5.4.
 *
 * **The log was written from the first migration and could not be read.** Two
 * ways out existed: the §9.6 audit pack, which is per-portfolio and quarterly,
 * and psql. So the questions an administrator is actually asked — *who turned
 * sender authentication off*, *who deleted that group*, *whose sign-in failed
 * eleven times last night* — had no answer inside the product, on an appliance
 * whose entire claim is that every act is recorded and explicable.
 *
 * **Newest first, and nothing on this screen writes.** There is no control here
 * that amends an entry, because a log with an edit path is not a log. What makes
 * the record checkable rather than merely asserted is the daily seal
 * (`audit.compute_digest`), which the pack carries.
 *
 * **Paged by cursor, not by page number.** The table grows at the newest end
 * while somebody is reading it, so an offset means a different set of rows each
 * time it is used — the reader would see entries twice and miss others, on the
 * one screen whose whole value is that nothing is missing. Going back is a stack
 * of cursors rather than arithmetic, for the same reason.
 *
 * **`before` and `after` are behind a disclosure.** Most rows are read for
 * *who* and *when*; the values matter on the few that are being investigated,
 * and printing every payload inline turns a page of history into a wall of JSON
 * nobody scans.
 */
export function Audit() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<number | null>(null)
  /** One id per page already visited. Its length is the page number, and its
   *  last element is the cursor that produced what is on screen. */
  const [trail, setTrail] = useState<(number | null)[]>([null])
  const [open, setOpen] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [actions, setActions] = useState<{ action: string; count: number }[]>([])
  const [actors, setActors] = useState<{ id: string; email: string }[]>([])
  const [action, setAction] = useState('')
  const [actor, setActor] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')
  const [size, setSize] = useState(100)

  useEffect(() => {
    Promise.all([api.auditActions(), api.auditActors()])
      .then(([a, who]) => {
        setActions(a)
        setActors(who)
      })
      // The filters are a convenience; the trail itself is the screen. Failing
      // to load them must not leave somebody looking at an error instead of
      // the log.
      .catch(() => undefined)
  }, [])

  const load = useCallback(
    async (cursor: number | null) => {
      setBusy(true)
      setError(null)
      try {
        const page = await api.auditTrail({
          action: action || undefined,
          actorUserId: actor || undefined,
          since: since || undefined,
          until: until || undefined,
          beforeId: cursor ?? undefined,
          limit: size,
        })
        setEntries(page.entries)
        setHasMore(page.has_more)
        setNextCursor(page.next_before_id)
        setOpen(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setBusy(false)
      }
    },
    [action, actor, since, until, size],
  )

  // A changed filter is a different question, so it starts at the newest entry
  // again. Keeping the cursor would page into the middle of a result set the
  // reader has never seen the start of.
  useEffect(() => {
    setTrail([null])
    void load(null)
  }, [load])

  function older() {
    if (nextCursor === null) return
    setTrail((t) => [...t, nextCursor])
    void load(nextCursor)
  }

  function newer() {
    if (trail.length < 2) return
    const back = trail.slice(0, -1)
    setTrail(back)
    void load(back[back.length - 1] ?? null)
  }

  const page = trail.length

  return (
    <div className="audit">
      <h2>Audit trail</h2>
      <p className="note">
        Every act this appliance recorded, newest first. Nothing on this screen
        changes anything — including the record itself, which is the point of
        keeping one. Each day is sealed with a digest that the audit pack carries,
        so a claim about what is here can be checked rather than taken on trust.
      </p>

      <div className="card filters">
        <label className="stacked">
          Kind of act
          <select value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">Everything</option>
            {/* The list comes off the log, so it offers what the appliance has
                actually done. A hard-coded list drifts the first time an action
                is added, and a filter missing an option looks exactly like a
                quiet log. */}
            {actions.map((a) => (
              <option key={a.action} value={a.action}>
                {a.action} ({a.count})
              </option>
            ))}
          </select>
        </label>
        <label className="stacked">
          By
          <select value={actor} onChange={(e) => setActor(e.target.value)}>
            <option value="">Anyone</option>
            {actors.map((a) => (
              <option key={a.id} value={a.id}>
                {a.email}
              </option>
            ))}
          </select>
        </label>
        <label className="stacked">
          From
          <input type="date" value={since} onChange={(e) => setSince(e.target.value)} />
        </label>
        <label className="stacked">
          Until
          <input
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            title="Exclusive — entries before the start of this day."
          />
        </label>
        <label className="stacked">
          Per page
          <select value={size} onChange={(e) => setSize(Number(e.target.value))}>
            {[25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {entries === null ? (
        <p className="loading">Reading the log…</p>
      ) : entries.length === 0 ? (
        <p className="empty" data-testid="no-entries">
          Nothing recorded matches that. The log is never empty on a working
          appliance — widen the dates, or clear the filters.
        </p>
      ) : (
        <table className="grid has-menus" data-testid="trail">
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>Did what</th>
              <th>To what</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <Fragment key={e.id}>
                <tr data-testid="entry">
                  <td className="clause">
                    {new Date(e.occurred_at).toLocaleString()}
                  </td>
                  <td>
                    {/* An act with no person behind it is the appliance itself
                        — a nightly seal, a mailbox poll. Saying so is better
                        than a blank, which reads as missing data on the one
                        screen where that would be alarming. */}
                    {e.actor_email ?? (
                      <span className="clause">the appliance</span>
                    )}
                  </td>
                  <td>{e.action}</td>
                  <td>
                    {e.subject ? (
                      <>
                        <strong>{e.subject}</strong>{' '}
                        <span className="clause">({e.entity_type})</span>
                      </>
                    ) : (
                      <span className="clause">{e.entity_type}</span>
                    )}
                  </td>
                  <td className="row-actions">
                    {/* One control per row rather than a button in a column of
                        its own. The width belongs to *Did what* and *To what*,
                        which is where the reading happens — and the menu is
                        where the two narrowing acts live, because "show me the
                        rest of what this person did" is the question a reader
                        arrives at from a row rather than from the filter bar. */}
                    <RowMenu
                      label={`Actions for ${e.action} on ${e.subject ?? e.entity_type}`}
                      testId={`entry-menu-${e.id}`}
                      items={[
                        {
                          label: open === e.id ? 'Hide what changed' : 'What changed',
                          onSelect: () => setOpen(open === e.id ? null : e.id),
                          disabled: !e.before && !e.after,
                          why: 'This act recorded no values — its action and subject are the whole of it.',
                        },
                        {
                          label: `Only ${e.action}`,
                          onSelect: () => setAction(e.action),
                        },
                        {
                          label: e.actor_email
                            ? `Only ${e.actor_email}`
                            : 'Only the appliance',
                          onSelect: () =>
                            setActor(
                              actors.find((a) => a.email === e.actor_email)?.id ?? '',
                            ),
                          disabled: !e.actor_email,
                          why: 'The appliance is not a person, so there is nobody to narrow to.',
                        },
                      ]}
                    />
                  </td>
                </tr>
                {open === e.id && (
                  <tr className="history-row">
                    <td colSpan={5}>
                      <Change before={e.before} after={e.after} />
                      <p className="clause">
                        Entry {e.id}
                        {e.ip && <> · from {e.ip}</>}
                        {e.request_id && <> · request {e.request_id}</>}
                      </p>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      <div className="actions pager">
        <button className="ghost" disabled={busy || page < 2} onClick={newer}>
          ← Newer
        </button>
        {/* A page number and no total. The table only grows, so a count is
            wrong by the time it renders — and expensive to be wrong about. */}
        <span className="note" data-testid="page">
          Page {page}
        </span>
        <button className="ghost" disabled={busy || !hasMore} onClick={older}>
          Older →
        </button>
      </div>
    </div>
  )
}

/**
 * What an entry changed, as fields rather than as a blob.
 *
 * The payloads are jsonb and their shape differs per action, so this reads the
 * union of the keys and shows each one's before and after. A raw `JSON.stringify`
 * would be honest and unreadable; the reader is looking for the one field that
 * moved.
 */
function Change({
  before,
  after,
}: {
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
}) {
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])]
  if (keys.length === 0) return <p className="note">No values were recorded.</p>

  const show = (v: unknown) =>
    v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v)

  return (
    <table className="grid" data-testid="change">
      <thead>
        <tr>
          <th>Field</th>
          <th>Before</th>
          <th>After</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((k) => {
          const was = before?.[k]
          const now = after?.[k]
          const moved = JSON.stringify(was) !== JSON.stringify(now)
          return (
            <tr key={k} className={moved ? undefined : 'unchanged'}>
              <td>{k}</td>
              <td className="clause">{show(was)}</td>
              <td className={moved ? 'limit' : 'clause'}>{show(now)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
